import * as React from 'react';
import {
  Box,
  Button,
  GDSStyleObject,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

export interface DrawerDialogProps {
  onClose: () => void;
  css?: GDSStyleObject;
  children: React.ReactNode;
  open?: boolean;
  id?: string;
  returnFocusRef?: React.RefObject<HTMLElement>;
  returnFocusOnClose?: boolean;
}
/**
 * A drawer dialog component that opens from the right side of the screen.
 * @param onClose - The function to call when the drawer is closed.
 * @param css - Additional CSS styles to apply to the drawer
 * @param children - The children to render inside the drawer.
 * @param open - Whether the drawer is open.
 * @param id - ID of the drawer element (used for aria-controls)
 * @param returnFocusRef - The ref to the element that should receive focus when the drawer is opened.
 * @param returnFocusOnClose - Whether to return focus to the element that received focus when the drawer is closed.
 */
export const DrawerDialog = ({
  onClose,
  css,
  children,
  open = false,
  id,
  returnFocusRef,
  returnFocusOnClose = true,
}: DrawerDialogProps) => {
  const { formatMessage } = useIntl();
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);
  const prevOpenRef = React.useRef(false);

  // Capture the returnFocusOnClose value when drawer is open to prevent race conditions
  const shouldReturnFocusRef = React.useRef(returnFocusOnClose);

  React.useEffect(() => {
    if (open) {
      shouldReturnFocusRef.current = returnFocusOnClose;
    }
  }, [open, returnFocusOnClose]);

  // Focus close button when drawer opens and track open state
  React.useEffect(() => {
    const justOpened = open && !prevOpenRef.current;

    // Update previous state
    prevOpenRef.current = open;

    if (justOpened) {
      // Small delay to ensure drawer is rendered and visible
      const timeoutId = setTimeout(() => {
        closeBtnRef.current?.focus({ preventScroll: true });
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [open]);

  // Return focus to trigger when drawer closes
  React.useEffect(() => {
    if (!open && shouldReturnFocusRef.current && returnFocusRef?.current) {
      // Wait for transition to complete, then check if we should return focus
      const timeoutId = setTimeout(() => {
        const currentFocus = document.activeElement;
        const focusIsOnBody = currentFocus === document.body;
        const focusIsOnClosedDrawer = currentFocus === closeBtnRef.current;

        // Return focus only if focus hasn't moved to another interactive element
        // (like another drawer's close button)
        if (
          (focusIsOnBody || focusIsOnClosedDrawer) &&
          returnFocusRef.current
        ) {
          returnFocusRef.current.focus({ preventScroll: true });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [open, returnFocusRef]);

  const handleCloseClick = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );
  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-hidden={!open}
      id={id}
      css={{
        position: 'fixed',
        insetBlockStart: 0,
        insetInlineEnd: 0,
        minHeight: '100vh',
        width: '50vw',
        overflow: 'visible',
        zIndex: '$modal',
        backgroundColor: '$surfaceBackgroundPrimary',
        borderInlineStart:
          'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
        boxShadow: '$dropdown',
        opacity: open ? 1 : 0,
        visibility: open ? 'visible' : 'hidden',
        transform: open ? 'translateX(0)' : 'translateX(8px)',
        pointerEvents: open ? 'auto' : 'none',
        transition:
          'opacity $defaultTime ease-in-out, visibility $defaultTime ease-in-out, transform $defaultTime ease-in-out',
        willChange: 'opacity, transform, visibility',
        ...css,
      }}
      onKeyDown={handleKeyDown}
    >
      <StackLayout
        orientation="vertical"
        css={{
          padding: '$one $twoAndHalf',
          overflow: 'auto',
          maxHeight: '100vh',
          transition:
            'opacity $defaultTime ease-in-out, transform $defaultTime ease-in-out',
        }}
      >
        <Button
          priority="tertiary"
          size="medium"
          quiet
          icon="interfaceClose"
          hideLabel
          css={{
            alignSelf: 'flex-end',
          }}
          onClick={handleCloseClick}
          ref={closeBtnRef}
        >
          {formatMessage({ id: 'CLOSE' })}
        </Button>
        <StackLayout
          orientation="vertical"
          spacing="$one"
          horizontalAlignment="stretch"
        >
          {children}
        </StackLayout>
      </StackLayout>
    </Box>
  );
};
